import { useCallback, useMemo, useState } from "react"
import { CHAIN_ID } from "@mezo/sdk"
import {
  AlertCircle,
  Block,
  Button,
  FlexGrid,
  FlexGridItem,
  HeadingXXLarge,
  LabelMedium,
  ParagraphMedium,
  ParagraphXSmall,
  Tag02,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { PayloadAction } from "@reduxjs/toolkit"
import { usePostHog } from "posthog-js/react"

import customModalBackground from "#/assets/store/taho-nft-modal-background.jpg"
import storeFullPreview from "#/assets/store/taho-og-nft.jpg"
import {
  claimTahoNFT,
  selectIsConnected,
  selectIsOnMezoChain,
  selectLoadingAccountData,
  selectUserHasLoan,
  selectUserLoan,
  setOpenModal,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  DEFAULT_MODAL_Z_INDEX,
  IS_MEZO_MAINNET,
  MEZO_NETWORK,
} from "#/shared/constants"
import { useTahoNFTClaim } from "#/shared/hooks"
import { PosthogEvent } from "#/shared/types"
import { networkUtils } from "#/shared/utils"

import { ConnectWalletButton, SwitchChainButton } from "../Button"
import { BaseModal, BaseModalProps } from "../Modal"

export default function MarketTahoNFTItemModal({
  isOpen,
  onClose,
}: BaseModalProps) {
  const isOnMezoChain = useDappSelector(selectIsOnMezoChain)
  const isConnected = useDappSelector(selectIsConnected)
  const loanInfo = useDappSelector(selectUserLoan)
  const userHasLoan = useDappSelector(selectUserHasLoan)
  const loadingAccountData = useDappSelector(selectLoadingAccountData)

  const [css, theme] = useStyletron()
  const dispatch = useDappDispatch()
  const posthog = usePostHog()

  const {
    isTaho,
    claimerId,
    claimSignature,
    hasValidClaimSignature,
    hasEnoughFunds,
    isClaimed,
    checkIsClaimed,
  } = useTahoNFTClaim()

  const [claimInProgress, setClaimInProgress] = useState(false)

  const isLoading =
    isTaho && (claimSignature === null || claimInProgress || loadingAccountData)

  const canClaimNFT =
    !isClaimed &&
    hasEnoughFunds &&
    hasValidClaimSignature &&
    isTaho &&
    userHasLoan &&
    !isLoading

  const claimNFT = useCallback(async () => {
    if (userHasLoan && loanInfo && claimerId && claimSignature && !isLoading) {
      setClaimInProgress(true)

      const { payload } = (await dispatch(
        claimTahoNFT({ id: claimerId, signature: claimSignature }),
      )) as PayloadAction<boolean>

      setClaimInProgress(false)
      if (payload) {
        dispatch(setOpenModal("taho-nft-store-item-success"))

        checkIsClaimed()

        posthog.capture(PosthogEvent.TAHO_NFT_CLAIMED, {
          taho_wallet_id: claimerId,
          borrowed_amount: loanInfo.debtAmount,
          collateral_amount: loanInfo.collateralAmount,
        })
      }
    }
  }, [
    claimSignature,
    checkIsClaimed,
    posthog,
    userHasLoan,
    loanInfo,
    claimerId,
    dispatch,
    isLoading,
  ])

  const hasCorrectChain = useMemo(() => isOnMezoChain, [isOnMezoChain])

  const buttonLabel = useMemo(() => {
    switch (true) {
      case !isTaho:
        return "Taho wallet users only!"
      // Taho install id available
      case isClaimed:
        return "Already claimed"
      case claimSignature === "not-eligible":
        return "User is not eligible"
      case claimSignature === "invalid-claim-address":
        return "Check wallet address"
      case !userHasLoan:
        return "Try borrowing first!"
      case !hasEnoughFunds:
        return "Not enough funds"
      default:
        return "Collect"
    }
  }, [hasEnoughFunds, isClaimed, claimSignature, userHasLoan, isTaho])

  const closeModalHandler = useCallback(() => {
    onClose?.({})
  }, [onClose])

  const renderCTAButton = () => {
    if (!isConnected) {
      return (
        <ConnectWalletButton
          width="100%"
          overrides={{
            BaseButton: { style: () => ({ marginTop: "80px" }) },
          }}
        >
          Sign in
        </ConnectWalletButton>
      )
    }

    if (!hasCorrectChain) {
      return (
        <SwitchChainButton
          label={IS_MEZO_MAINNET ? "Mezo" : "Mezo Testnet"}
          chainId={CHAIN_ID[MEZO_NETWORK]}
          size="large"
          width="100%"
        />
      )
    }

    return (
      <Button
        size="large"
        onClick={claimNFT}
        isLoading={isLoading}
        disabled={!canClaimNFT}
        width="100%"
        data-attr="taho_nft-step_2"
      >
        {buttonLabel}
      </Button>
    )
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={closeModalHandler}
      closeable
      overrides={{
        Root: { style: () => ({ zIndex: DEFAULT_MODAL_Z_INDEX }) },
        Dialog: {
          style: () => ({
            width: "100%",
            height: "100vh",
            margin: 0,
            borderRadius: 0,
          }),
        },
      }}
    >
      <FlexGrid
        flexGridColumnCount={2}
        height="100%"
        backgroundColor={theme.colors.white}
      >
        <FlexGridItem position="relative" overflow="hidden">
          <Block
            as="img"
            src={customModalBackground}
            height="100%"
            width="100%"
            className={css({ objectFit: "cover" })}
          />
          <Block
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            position="absolute"
            top="50%"
            left="50%"
            padding={theme.sizing.scale1000}
            className={css({
              transform: "translate(-50%, -50%)",
            })}
          >
            <Block
              as="img"
              src={storeFullPreview}
              className={css({
                maxWidth: "505px",
                objectFit: "contain",
                borderRadius: "17px",
                overflow: "hidden",
                boxShadow: "0px 4px 12.1px 10px rgba(209, 209, 209, 0.25)",
              })}
            />
          </Block>
        </FlexGridItem>
        <FlexGridItem
          padding="92px 176px 40px 74px"
          backgroundColor={theme.colors.white}
        >
          <Block
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
          >
            <Block>
              <Block paddingRight={theme.sizing.scale800}>
                <HeadingXXLarge marginBottom={theme.sizing.scale600}>
                  Taho x Mezo NFT
                </HeadingXXLarge>
                <ParagraphMedium
                  color={theme.colors.contentTertiary}
                  marginBottom={theme.sizing.scale800}
                >
                  For loyal Taho users who borrowed MUSD on Mezo{" "}
                  {networkUtils.getMezoNetworkLabel()}.
                </ParagraphMedium>
                <Button
                  $as="div"
                  kind="secondary"
                  size="medium"
                  shape="pill"
                  startEnhancer={
                    <Tag02 size={16} color={theme.colors.contentPrimary} />
                  }
                  overrides={{
                    BaseButton: { style: () => ({ pointerEvents: "none" }) },
                  }}
                >
                  <LabelMedium>100 MUSD</LabelMedium>
                </Button>
              </Block>
              <Block marginTop="80px">{renderCTAButton()}</Block>
            </Block>
            <ParagraphXSmall
              color={theme.colors.contentTertiary}
              display="flex"
              alignItems="center"
              className={css({ gap: theme.sizing.scale100 })}
            >
              <AlertCircle color={theme.colors.gray400} size={14} />
              Image is a placeholder, final product may differ
            </ParagraphXSmall>
          </Block>
        </FlexGridItem>
      </FlexGrid>
    </BaseModal>
  )
}
